import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarRange, FileStack } from 'lucide-react';
import { get, post } from '@/lib/api';
import { useBranchLock } from '@/lib/branch';
import { describeError, useAuth } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { insuranceTabPath } from './insurance-routes';

const monthStart = () => `${new Date().toISOString().slice(0, 8)}01`;
const today = () => new Date().toISOString().slice(0, 10);

export function ClaimPeriodPage() {
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), insuranceTabPath('claims'));
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const branches = user?.branches ?? [];
  const [payerId, setPayerId] = useState('');
  const lock = useBranchLock();
  const [branchId, setBranchId] = useState(lock.branchId);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [error, setError] = useState<string | null>(null);
  const payers = useQuery({ queryKey: ['payers'], queryFn: () => get<{ id: string; name: string }[]>('/insurance/payers') });
  const run = useMutation({
    mutationFn: () => post<{ invoices: number; claims: { number: string }[]; failed: { message: string }[] }>('/insurance/claims/bulk', { payerId, from, to, ...(branchId ? { branchId } : {}) }),
    onSuccess: (result) => {
      toast.success(`${result.claims.length} claim${result.claims.length === 1 ? '' : 's'} made from ${result.invoices} bills`, result.failed.length ? `${result.failed.length} could not be made: ${result.failed[0]?.message ?? ''}` : undefined);
      void queryClient.invalidateQueries({ queryKey: ['claims'] });
      void queryClient.invalidateQueries({ queryKey: ['unclaimed'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });
  const ready = Boolean(payerId && from && to);
  const dirty = !run.isSuccess && (Boolean(payerId) || Boolean(branchId) || from !== monthStart() || to !== today());

  return (
    <FormScreen
      title="Claim a period"
      description="Makes one claim per patient and branch from every unclaimed bill of the payer in the period"
      icon={FileStack}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) run.mutate();
      }}
    >
      <FormSection title={tr('billing', 'claim')} icon={FileStack}>
        <Field label="Payer" required>
          <Select value={payerId} onChange={(e) => setPayerId(e.target.value)}>
            <option value="">Choose…</option>
            {payers.data?.map((payer) => (
              <option key={payer.id} value={payer.id}>
                {payer.name}
              </option>
            ))}
          </Select>
        </Field>
        {branches.length > 1 && (
          <Field label="Branch">
            <Select value={branchId} readOnly={lock.locked} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Every branch, one claim each</option>
              {branches.map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormSection>
      <FormSection title={tr('billing', 'period')} icon={CalendarRange}>
        <Field label="Bills issued from" required>
          <DatePicker value={from} onChange={(value) => setFrom(value)} />
        </Field>
        <Field label="to" required>
          <DatePicker value={to} onChange={(value) => setTo(value)} />
        </Field>
      </FormSection>
      {error && <Alert variant="destructive">{error}</Alert>}
      <FormActions submitLabel="Make the claims" loading={run.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
